import React from "react"
import { motion } from "framer-motion"
import { X, DollarSign } from "lucide-react"

const FilterModal = ({ filters, setFilters, onClose, categories, brands }) => {
  const handleCheckboxChange = (filterType, value) => {
    const updatedFilters = { ...filters }
    const index = updatedFilters[filterType].indexOf(value)
    if (index > -1) {
      updatedFilters[filterType].splice(index, 1)
    } else {
      updatedFilters[filterType].push(value)
    }
    setFilters(updatedFilters)
  }

  const handlePriceRangeChange = (min, max) => {
    setFilters({ ...filters, priceRange: { min, max } })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        className="bg-white rounded-lg p-4 w-full max-w-sm max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Filters</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-1 text-start">Categories</h3>
            <div className="space-y-2">
              {categories.map((category) => (
                <label key={category.id} className="flex items-center text-start">
                  <input
                    type="checkbox"
                    checked={filters.categories.includes(category.id)}
                    onChange={() => handleCheckboxChange("categories", category.id)}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm">{category.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-1 text-start">Brands</h3>
            <div className="space-y-2">
              {brands.map((brand) => (
                <label key={brand.id} className="flex items-center text-start">
                  <input
                    type="checkbox"
                    checked={filters.brands.includes(brand.id)}
                    onChange={() => handleCheckboxChange("brands", brand.id)}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm">{brand.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-1 text-start">Status</h3>
            <div className="space-y-2">
              <label className="flex items-center text-start">
                <input
                  type="checkbox"
                  checked={filters.status.includes("active")}
                  onChange={() => handleCheckboxChange("status", "active")}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm">Active</span>
              </label>
              <label className="flex items-center text-start">
                <input
                  type="checkbox"
                  checked={filters.status.includes("inactive")}
                  onChange={() => handleCheckboxChange("status", "inactive")}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm">Inactive</span>
              </label>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-1 text-start">Price Range</h3>
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <DollarSign className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={filters.priceRange.min}
                  onChange={(e) => handlePriceRangeChange(Number(e.target.value), filters.priceRange.max)}
                  className="pl-7 pr-2 py-1 text-sm border rounded-md w-full"
                  placeholder="Min"
                />
              </div>
              <div className="relative flex-1">
                <DollarSign className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={filters.priceRange.max}
                  onChange={(e) => handlePriceRangeChange(filters.priceRange.min, Number(e.target.value))}
                  className="pl-7 pr-2 py-1 text-sm border rounded-md w-full"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-1 text-start">Special Filters</h3>
            <div className="space-y-2">
              <label className="flex items-center text-start">
                <input
                  type="checkbox"
                  checked={filters.isTopSeller}
                  onChange={() => setFilters({ ...filters, isTopSeller: !filters.isTopSeller })}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm">Top Seller</span>
              </label>
              <label className="flex items-center text-start">
                <input
                  type="checkbox"
                  checked={filters.isFeatured}
                  onChange={() => setFilters({ ...filters, isFeatured: !filters.isFeatured })}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm">Featured</span>
              </label>
              <label className="flex items-center text-start">
                <input
                  type="checkbox"
                  checked={filters.isSpecialOffer}
                  onChange={() => setFilters({ ...filters, isSpecialOffer: !filters.isSpecialOffer })}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm">Special Offer</span>
              </label>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Apply Filters
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default FilterModal

